/**
 * 登录账号与 Client KYB id 的本地绑定（localStorage）
 * 用于 getClientKybInfo / postClientKybUpdate 等按 id 访问
 */

const STORAGE_KEY = 'mpw_client_kyb_binding';

export type ClientKybBinding = {
  /** 最近一次登录用户名 */
  lastAccount: string;
  lastEmail?: string;
  /** 按登录账号维度：kybId 来自 /client/compliance/kyb/add 返回的 data.id */
  bindings: Record<string, { kybId?: string; email?: string }>;
};

function readRaw(): ClientKybBinding {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { lastAccount: '', bindings: {} };
    }
    const parsed = JSON.parse(raw) as ClientKybBinding;
    return {
      lastAccount: parsed.lastAccount || '',
      lastEmail: parsed.lastEmail,
      bindings: parsed.bindings && typeof parsed.bindings === 'object' ? parsed.bindings : {},
    };
  } catch {
    return { lastAccount: '', bindings: {} };
  }
}

function writeRaw(data: ClientKybBinding) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/** 注册成功后：记录账号与邮箱（此时尚无 kybId，首次登录再创建） */
export function persistRegisterAccount(params: { username: string; email: string }) {
  const state = readRaw();
  const username = params.username.trim();
  const email = params.email.trim();
  state.lastAccount = username;
  state.lastEmail = email;
  state.bindings[username] = {
    ...state.bindings[username],
    email,
  };
  writeRaw(state);
}

/** 登录成功后：更新当前登录账号（保留已有 kybId / 注册时写入的 email） */
export function persistLoginAccount(params: { username: string }) {
  const state = readRaw();
  const username = params.username.trim();
  state.lastAccount = username;
  if (!state.bindings[username]) {
    state.bindings[username] = {};
  }
  writeRaw(state);
}

export function getKybIdForAccount(account: string): string | undefined {
  const id = account?.trim();
  if (!id) return undefined;
  return readRaw().bindings[id]?.kybId;
}

export function setKybIdForAccount(account: string, kybId: string) {
  const state = readRaw();
  const username = account.trim();
  state.bindings[username] = {
    ...state.bindings[username],
    kybId,
  };
  writeRaw(state);
}

/** 最近一次登录的用户名 */
export function getActiveLoginAccount(): string | undefined {
  const a = readRaw().lastAccount?.trim();
  return a || undefined;
}

/**
 * 解析 KYB 详情/修改应使用的 id：显式传入 > 当前账号已绑定 > 兜底 current（需后端支持）
 */
export function getResolvedKybIdForRequest(overrideId?: string): string {
  if (overrideId?.trim()) return overrideId.trim();
  const account = getActiveLoginAccount();
  const bound = account ? getKybIdForAccount(account) : undefined;
  if (bound?.trim()) return bound.trim();
  return 'current';
}
